import { forwardRef, type ReactNode } from 'react';
import { Checkbox as HeroCheckbox } from '@heroui/react/checkbox';
import { cn } from '@/utils/cn';

/**
 * Checkbox — HeroUI's, built on react-aria.
 *
 * The whole control (box + label) is one focusable, clickable target with the
 * label properly associated, which the previous Radix version did not do: it
 * rendered a bare `<label>` with no `htmlFor`.
 *
 * Binding it to react-hook-form takes a `Controller`, not `{...register()}`.
 * This is a controlled react-aria component driven by `isSelected` and
 * `onChange(boolean)`; `register()` hands over a ref and an event handler
 * that have nothing to attach to, so the field silently never registers a
 * value. See `Login` for the shape.
 */
export interface CheckboxProps {
  /** Controlled checked state. `'indeterminate'` renders the mixed state. */
  checked?: boolean | 'indeterminate';
  defaultChecked?: boolean;
  onCheckedChange?: (checked: boolean) => void;
  label?: ReactNode;
  children?: ReactNode;
  disabled?: boolean;
  readOnly?: boolean;
  required?: boolean;
  name?: string;
  value?: string;
  id?: string;
  /**
   * Accessible name for a checkbox with no visible `label` — a selection
   * column in a table, typically, where the label would repeat in every row.
   * One of `label` or `aria-label` is always required.
   */
  'aria-label'?: string;
  /**
   * Blur handler — for `Controller`'s `field.onBlur`, so touched/blur
   * validation works on a checkbox bound through react-hook-form.
   */
  onBlur?: (e: React.FocusEvent<Element>) => void;
  /**
   * react-aria slot to bind to.
   *
   * Inside a Table, react-aria publishes a CheckboxContext that expects
   * `slot="selection"` and throws "A slot prop is required" for any checkbox
   * that does not name one. Pass `null` for a checkbox that manages its own
   * state inside a table cell — a selection column driven by the page rather
   * than by the Table's own selection manager.
   */
  slot?: string | null;
  className?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      checked,
      defaultChecked,
      onCheckedChange,
      onBlur,
      label,
      children,
      disabled,
      readOnly,
      required,
      className,
      name,
      value,
      ...props
    },
    ref,
  ) => {
    const isIndeterminate = checked === 'indeterminate';

    return (
      <HeroCheckbox
        ref={ref}
        name={name}
        value={value}
        isSelected={isIndeterminate ? false : checked}
        isIndeterminate={isIndeterminate}
        defaultSelected={defaultChecked}
        onChange={onCheckedChange}
        onBlur={onBlur}
        isDisabled={disabled}
        isReadOnly={readOnly}
        isRequired={required}
        className={cn('flex items-center gap-2', className)}
        {...props}
      >
        <HeroCheckbox.Content>
          <HeroCheckbox.Control>
            <HeroCheckbox.Indicator />
          </HeroCheckbox.Control>
          {(label ?? children) && <span>{label ?? children}</span>}
        </HeroCheckbox.Content>
      </HeroCheckbox>
    );
  },
);
Checkbox.displayName = 'Checkbox';
